import type { FindingAnchor } from '@gis/bcf';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';

/** What was typed in the New Finding form, kept while the user picks again. */
export type FindingDraft = { title: string; description: string; guestName?: string };

/**
 * The New Finding tool's state: off, waiting for a pick in 3D, or showing
 * the form for a pick (`anchor`) or for the view alone (`anchor: null`).
 * `draft` carries the form's text through a "Pick again".
 */
export type FindingsMode =
  | { phase: 'idle' }
  | { phase: 'picking'; draft?: FindingDraft }
  | { phase: 'form'; anchor: FindingAnchor | null; draft?: FindingDraft };

export const findingsModeAtom = atomFamily((_projectId: string) => atom<FindingsMode>({ phase: 'idle' }));

/** The finding open in the detail view (Task 8), e.g. the one just created. */
export const selectedFindingIdAtom = atomFamily((_projectId: string) => atom<string | null>(null));

/** Whether the finding pins are drawn in 3D. */
export const findingPinsVisibleAtom = atomFamily((_projectId: string) => atom(true));

/** Whether the findings dock (Task 8) is open. */
export const findingsDockOpenAtom = atomFamily((_projectId: string) => atom(false));

/**
 * True while the New Finding tool owns the left click (any project — the
 * viewer shows one at a time). Folded into `toolPickSuppressedAtom` so a
 * pick does not also open a selection drawer.
 */
export const newFindingPickingAtom = atom(false);

/** The findings dock's filters (spec §9). Shared with the pins: a pin is drawn
 *  for every finding the dock lists. */
export type FindingsFilter = {
  status: 'OPEN' | 'CLOSED' | 'ALL';
  authorKind: 'ALL' | 'MEMBER' | 'LINK' | 'IMPORTED';
  /** null = any label. */
  label: string | null;
  text: string;
  /** A hub status as the file wrote it (`bcfStatusRaw`); null = any. */
  statusRaw: string | null;
  /** A topic type; null = any. */
  topicType: string | null;
  /**
   * Where the findings came from: made here (`NATIVE`), from one imported
   * file (`FILE:<name>`, created or last updated from it), imported before
   * file names were kept (`IMPORTED_UNKNOWN`), or anywhere (`ALL`).
   */
  source: FindingsSourceFilter;
  /** Only findings changed since they were last exchanged (or never exchanged). */
  changedOnly: boolean;
};

export type FindingsSourceFilter = 'ALL' | 'NATIVE' | 'IMPORTED_UNKNOWN' | `FILE:${string}`;

export const DEFAULT_FINDINGS_FILTER: FindingsFilter = {
  status: 'OPEN',
  authorKind: 'ALL',
  label: null,
  text: '',
  statusRaw: null,
  topicType: null,
  source: 'ALL',
  changedOnly: false,
};

/**
 * Where a viewer's filter starts: Open for the firm, which works the open
 * list; All for a share-link visitor, who comes to see what was raised and
 * would otherwise never see a finding the firm has resolved.
 */
export const defaultFindingsFilter = (isLink: boolean): FindingsFilter =>
  isLink ? LINK_FINDINGS_FILTER : DEFAULT_FINDINGS_FILTER;

const LINK_FINDINGS_FILTER: FindingsFilter = { ...DEFAULT_FINDINGS_FILTER, status: 'ALL' };

/**
 * The filter the user set, or null while it is still the viewer's default —
 * read it through `resolveFindingsFilter`, which knows whether this is a share
 * link (the atom does not: `getLinkSessionId` lives with the GraphQL client).
 */
export const findingsFilterAtom = atomFamily((_projectId: string) => atom<FindingsFilter | null>(null));

export const resolveFindingsFilter = (stored: FindingsFilter | null, isLink: boolean): FindingsFilter =>
  stored ?? defaultFindingsFilter(isLink);

/** The dock's ticked rows (multi-select, used by BCF export). */
export const findingsCheckedAtom = atomFamily((_projectId: string) => atom<ReadonlySet<string>>(new Set<string>()));
